import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Paperclip } from 'lucide-react';
import { TableActionButton, TableActionGroup } from '@channelplay/table-actions';
import { ewsDashboardAPI } from '../../services/api';
import { InlineLoader } from '../loading';
import { Modal } from './DashboardPrimitives';
import {
  messageFrom,
  ticketChannel,
  ticketStatus,
  ticketWhen,
  ticketsTitle,
  type EwsConversation,
  type EwsTicket,
} from './ews';

/**
 * Dashboard → EWS: a ticket count, opened.
 *
 * The tickets one employee raised in one month — exactly the ones the number counted —
 * and, for each, its conversation as the employee saw it in the app. Read from Helpdesk
 * through this server, which answers only a viewer who holds EWS and only about people EWS
 * lists for them. Files are named, not downloadable: Helpdesk's report sends no file.
 */

const TH = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500';
const TH_ACTIONS = 'px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500';
const TD = 'px-3 py-2 text-sm text-gray-700';
const TD_NUMBER = 'whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-900';

const failureOf = (err: unknown): string =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Helpdesk could not be read just now. Try again in a minute.';

function StatusChip({ status }: { status: string }) {
  const s = ticketStatus(status);
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${s.tone}`}>{s.label}</span>;
}

export default function EwsTicketsModal({ projectId, employee, month, count, onClose }: {
  projectId: number;
  employee: { code: string; name: string };
  /** 'YYYY-MM' */
  month: string;
  /** The number that was clicked, for the title while the list loads. */
  count: number;
  onClose: () => void;
}) {
  const [tickets, setTickets] = useState<EwsTicket[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [conversation, setConversation] = useState<EwsConversation | null>(null);
  const [talkError, setTalkError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const askRef = useRef(0);

  useEffect(() => {
    let alive = true;
    ewsDashboardAPI.getTickets(projectId, { empCode: employee.code, month })
      .then((res) => { if (alive) setTickets((res.data?.data?.tickets ?? []) as EwsTicket[]); })
      .catch((err) => { if (alive) setListError(failureOf(err)); });
    return () => { alive = false; };
  }, [projectId, employee.code, month]);

  const openConversation = (ticketNumber: string) => {
    const ask = ++askRef.current;
    setOpen(ticketNumber);
    setConversation(null);
    setTalkError(null);
    ewsDashboardAPI.getConversation(projectId, ticketNumber)
      .then((res) => { if (ask === askRef.current) setConversation(res.data?.data as EwsConversation); })
      .catch((err) => { if (ask === askRef.current) setTalkError(failureOf(err)); });
  };

  // A long ticket comes in parts: read on from the last message shown.
  const readMore = () => {
    if (!conversation || !open) return;
    const ask = askRef.current;
    const last = conversation.nextAfter ?? conversation.messages[conversation.messages.length - 1]?.id ?? 0;
    setLoadingMore(true);
    ewsDashboardAPI.getConversation(projectId, open, last)
      .then((res) => {
        if (ask !== askRef.current) return;
        const next = res.data?.data as EwsConversation;
        setConversation((cur) => (cur ? { ...cur, messages: [...cur.messages, ...next.messages], hasMore: next.hasMore } : next));
      })
      .catch((err) => { if (ask === askRef.current) setTalkError(failureOf(err)); })
      .finally(() => setLoadingMore(false));
  };

  const back = () => {
    askRef.current += 1;
    setOpen(null);
    setConversation(null);
    setTalkError(null);
  };

  const shownCount = tickets?.length ?? count;
  const title = open
    ? `${open}${conversation?.ticket.subject ? ` · ${conversation.ticket.subject}` : ''}`
    : `${employee.name} · ${ticketsTitle(shownCount, month)}`;

  return (
    <Modal title={title} onClose={onClose} maxWidth="max-w-3xl">
      {open === null ? (
        <>
          {listError && <p className="text-sm text-rose-700">{listError}</p>}
          {!listError && tickets === null && <InlineLoader label="Reading the tickets from Helpdesk…" />}
          {tickets !== null && tickets.length === 0 && (
            <p className="text-sm text-gray-500">No ticket raised in this month was found in Helpdesk.</p>
          )}
          {tickets !== null && tickets.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={TH}>Ticket</th>
                    <th className={TH}>Subject</th>
                    <th className={TH}>Channel</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Raised</th>
                    <th className={TH_ACTIONS}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tickets.map((t) => (
                    <tr key={t.ticketNumber}>
                      <td className={TD_NUMBER}>{t.ticketNumber}</td>
                      <td className={TD}>{t.subject || '—'}</td>
                      <td className={`${TD} whitespace-nowrap`}>{ticketChannel(t.channel)}</td>
                      <td className={TD}><StatusChip status={t.status} /></td>
                      <td className={`${TD} whitespace-nowrap`}>{ticketWhen(t.createdAt)}</td>
                      <td className="px-3 py-2 text-center">
                        <TableActionGroup>
                          <TableActionButton
                            action="message"
                            label={`View the conversation of ${t.ticketNumber}`}
                            title="View the conversation"
                            onClick={() => openConversation(t.ticketNumber)}
                          />
                        </TableActionGroup>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={back}
              className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100"
            >
              <ArrowLeft className="h-4 w-4" />
              All tickets
            </button>
            {conversation && <StatusChip status={conversation.ticket.status} />}
          </div>
          {talkError && <p className="text-sm text-rose-700">{talkError}</p>}
          {!talkError && conversation === null && <InlineLoader label="Reading the conversation from Helpdesk…" />}
          {conversation && conversation.messages.length === 0 && (
            <p className="text-sm text-gray-500">No messages on this ticket.</p>
          )}
          {conversation && conversation.messages.length > 0 && (
            <ul className="flex flex-col gap-2" aria-label="Conversation">
              {conversation.messages.map((m) => {
                // The employee's side: the employee and anyone else who wrote on their side
                // (a colleague copied on an email), as Helpdesk's note draws it.
                const mine = m.from === 'employee' || m.from === 'other';
                return (
                  <li
                    key={m.id}
                    className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${mine ? 'self-end bg-primary-50 text-gray-900' : 'self-start bg-gray-100 text-gray-800'}`}
                  >
                    <p className="whitespace-pre-wrap break-words">{m.text}</p>
                    {m.attachments.length > 0 && (
                      <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-gray-500">
                        {m.attachments.map((name, i) => (
                          <span key={`${name}-${i}`} className="inline-flex items-center gap-1"><Paperclip className="h-3 w-3" />{name}</span>
                        ))}
                      </p>
                    )}
                    <p className="mt-1 text-[11px] text-gray-500">{messageFrom(m.from)} · {ticketWhen(m.createdAt, true)}</p>
                  </li>
                );
              })}
            </ul>
          )}
          {conversation?.hasMore && (
            <button
              type="button"
              onClick={readMore}
              disabled={loadingMore}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              {loadingMore ? 'Reading…' : 'Show later messages'}
            </button>
          )}
        </div>
      )}
    </Modal>
  );
}
